/**
 * Super Admin impersonation state.
 *
 * Stored in sessionStorage so it survives page reloads (and the 60-min JWT
 * refresh cycle) but is cleared when the tab/window closes. Deliberately has
 * NO React dependency so the API client can read it at request build time.
 */

export interface ImpersonationTarget {
  id: number;
  username: string;
  email: string;
  name: string;
  organization: { id: string; name: string } | null;
}

export interface ImpersonationActor {
  id: number;
  username: string;
  email: string;
  name: string;
}

export interface ImpersonationState {
  target: ImpersonationTarget;
  impersonator: ImpersonationActor | null;
}

const STORAGE_KEY = 'apexos:impersonate';

export function getImpersonationState(): ImpersonationState | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ImpersonationState;
    if (!parsed?.target?.id) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function getImpersonatedUserId(): number | null {
  return getImpersonationState()?.target?.id ?? null;
}

export function setImpersonation(state: ImpersonationState): void {
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
}

export function clearImpersonation(): void {
  sessionStorage.removeItem(STORAGE_KEY);
}
